import type { BannerForm, BannerItem } from "./types";

export const STATUS_OPTIONS = ["planned", "progress", "in-progress", "ended", "closed"] as const;
export const CATEGORY_OPTIONS = [
  "critical",
  "priority",
  "high",
  "medium",
  "low",
  "bug fix",
  "upgrade",
  "release",
] as const;

export const EMPTY_BANNER_FORM: BannerForm = {
  name: "",
  description: "",
  release_notes: "",
  deployment_status: "",
  deployment_category: "",
  deployment_start_time: "",
  deployment_end_time: "",
  banner_start_time: "",
  banner_end_time: "",
};

export function cloneBannerForm(form: BannerForm): BannerForm {
  return structuredClone(form);
}

export function datetimeLocalNowTruncatedToMinutes(date: Date = new Date()): string {
  const t = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return t.toISOString().slice(0, 16);
}

export interface ValidateBannerFormOptions {
  mode?: "create" | "edit";
}

export function withDeploymentStartSynced(
  form: BannerForm,
  deploymentStartTime: string
): BannerForm {
  const next: BannerForm = { ...form, deployment_start_time: deploymentStartTime };
  if (!deploymentStartTime.trim()) {
    next.deployment_end_time = "";
    return next;
  }
  if (!next.deployment_end_time.trim()) return next;
  const startMs = new Date(deploymentStartTime).getTime();
  const endMs = new Date(next.deployment_end_time).getTime();
  if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs < startMs) {
    next.deployment_end_time = "";
  }
  return next;
}

export function withBannerStartSynced(form: BannerForm, bannerStartTime: string): BannerForm {
  const next: BannerForm = { ...form, banner_start_time: bannerStartTime };
  if (!bannerStartTime.trim()) {
    next.banner_end_time = "";
    return next;
  }
  if (!next.banner_end_time.trim()) return next;
  const startMs = new Date(bannerStartTime).getTime();
  const endMs = new Date(next.banner_end_time).getTime();
  if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs < startMs) {
    next.banner_end_time = "";
  }
  return next;
}

export function toDateTimeInputValue(isoValue?: string | null): string {
  if (!isoValue) return "";
  const date = new Date(isoValue);
  if (Number.isNaN(date.getTime())) return "";
  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

export function toApiIsoValue(inputValue?: string | null): string {
  if (!inputValue) return "";
  const date = new Date(inputValue);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString();
}

export function formatTableDateTime(isoValue?: string | null): string {
  if (!isoValue) return "—";
  const date = new Date(isoValue);
  if (Number.isNaN(date.getTime())) return "—";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

export function toForm(item: BannerItem): BannerForm {
  return {
    banner_id: item.banner_id,
    name: item.name ?? "",
    description: item.description ?? "",
    release_notes: item.release_notes ?? "",
    deployment_status: item.deployment_status ?? "",
    deployment_category: item.deployment_category ?? "",
    deployment_start_time: toDateTimeInputValue(item.deployment_start_time),
    deployment_end_time: toDateTimeInputValue(item.deployment_end_time),
    banner_start_time: toDateTimeInputValue(item.banner_start_time),
    banner_end_time: toDateTimeInputValue(item.banner_end_time),
  };
}

export function normalizeLabel(value: string): string {
  if (!value) return "—";
  return value
    .split(" ")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function isBannerFormMandatoryFilled(form: BannerForm): boolean {
  return (
    form.name.trim() !== "" &&
    form.deployment_status.trim() !== "" &&
    form.deployment_category.trim() !== "" &&
    form.deployment_start_time.trim() !== "" &&
    form.deployment_end_time.trim() !== "" &&
    form.banner_start_time.trim() !== "" &&
    form.banner_end_time.trim() !== ""
  );
}

export function validateForm(
  form: BannerForm,
  opts?: ValidateBannerFormOptions
): Partial<Record<keyof BannerForm, string>> {
  const errors: Partial<Record<keyof BannerForm, string>> = {};
  const mode = opts?.mode ?? "create";
  const now = new Date();

  if (!form.name.trim()) errors.name = "Subject is required.";
  if (!form.deployment_status.trim()) errors.deployment_status = "Deployment status is required.";
  if (!form.deployment_category.trim())
    errors.deployment_category = "Deployment category is required.";
  if (!form.deployment_start_time)
    errors.deployment_start_time = "Deployment start date is required.";
  if (!form.deployment_end_time) errors.deployment_end_time = "Deployment end date is required.";
  if (!form.banner_start_time) errors.banner_start_time = "Banner start date is required.";
  if (!form.banner_end_time) errors.banner_end_time = "Banner end date is required.";

  const deploymentStart = form.deployment_start_time ? new Date(form.deployment_start_time) : null;
  const deploymentEnd = form.deployment_end_time ? new Date(form.deployment_end_time) : null;
  const bannerStart = form.banner_start_time ? new Date(form.banner_start_time) : null;
  const bannerEnd = form.banner_end_time ? new Date(form.banner_end_time) : null;

  if (
    mode === "create" &&
    deploymentStart &&
    !Number.isNaN(deploymentStart.getTime()) &&
    deploymentStart < now
  ) {
    errors.deployment_start_time = "Deployment start cannot be before the current date and time.";
  }

  if (
    mode === "create" &&
    bannerStart &&
    !Number.isNaN(bannerStart.getTime()) &&
    bannerStart < now
  ) {
    errors.banner_start_time = "Banner start cannot be before the current date and time.";
  }

  if (
    deploymentStart &&
    deploymentEnd &&
    !Number.isNaN(deploymentStart.getTime()) &&
    !Number.isNaN(deploymentEnd.getTime()) &&
    deploymentEnd < deploymentStart
  ) {
    errors.deployment_end_time =
      "Deployment end datetime cannot be earlier than Deployment Start datetime.";
  }

  if (
    bannerStart &&
    bannerEnd &&
    !Number.isNaN(bannerStart.getTime()) &&
    !Number.isNaN(bannerEnd.getTime()) &&
    bannerEnd < bannerStart
  ) {
    errors.banner_end_time = "Banner end datetime cannot be earlier than Banner Start datetime.";
  }

  if (
    deploymentEnd &&
    bannerEnd &&
    !Number.isNaN(deploymentEnd.getTime()) &&
    !Number.isNaN(bannerEnd.getTime()) &&
    deploymentEnd > bannerEnd
  ) {
    errors.deployment_end_time =
      "Deployment end datetime cannot be later than Banner End datetime.";
  }

  return errors;
}
